import * as React from 'react';
import { Alert, Button, Checkbox, Modal } from '@ixicare/ui';
import { DateRange, DayPicker } from 'react-day-picker';
import { useMutation } from '@tanstack/react-query';
import { endOfToday, subYears } from 'date-fns';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';
import { humaniseErrors } from '~utils/error';
import { DBGWithType } from '~modules/devices/components/DeviceHealthTable/types';
import styles from '~/components/ActionBar/ActionBar.module.css';
import classnames from 'classnames';
import { Device, DeleteDeviceDataParams } from '@ixicare/resources';

const MAX_CHIPS = 7;

interface DeleteDeviceDataModalProps {
  devices: DBGWithType[];
  onClose: () => void;
  onRefresh: () => void;
}

export function DeleteDeviceDataModal({ devices, onClose, onRefresh }: DeleteDeviceDataModalProps) {
  const [range, setRange] = React.useState<DateRange>();
  const [clearBilling, setClearBilling] = React.useState(false);

  const yearAgo = React.useMemo(() => subYears(new Date(), 10), []);
  const currentDay = React.useMemo(() => endOfToday(), []);

  const shownDevices = devices.slice(0, MAX_CHIPS);
  const overflow = devices.length - shownDevices.length;

  const mutation = useMutation({
    mutationFn: async () => {
      const params: DeleteDeviceDataParams = {
        startDate: range?.from,
        endDate: range?.to,
        clearBillingEvents: clearBilling,
      };
      await Promise.all(devices.map((d) => Device.deleteDeviceData(Number(d.id), params)));
    },
    onSuccess: () => {
      onRefresh();
      onClose();
    },
  });

  return (
    <Modal onClose={onClose} style={{ maxWidth: 450 }}>
      <h3 className="text-center mb-10">{t('device.deleteData.title')}</h3>
      <ul className={styles.chips}>
        {shownDevices.map((device) => (
          <li className={classnames('text-center', 'mb-10', styles.chip)} key={device.macAddress}>
            {device.macAddress} - {device.description}
          </li>
        ))}
        {overflow > 0 && <li className={classnames('text-center', styles.chip)}>+{overflow}</li>}
      </ul>
      {mutation.error && <Alert type="error">{humaniseErrors(mutation.error as Error).title}</Alert>}
      <p className="text-muted mb-15">{t('device.deleteData.dateRange')}</p>
      <DayPicker
        ISOWeek
        mode="range"
        selected={range}
        onSelect={setRange}
        captionLayout="dropdown"
        fixedWeeks
        showOutsideDays
        startMonth={yearAgo}
        endMonth={currentDay}
      />
      <Spacer size={16} />
      <Checkbox
        id="clear-billing-events"
        label={t('device.deleteData.clearBillingEvents')}
        checked={clearBilling}
        onCheckedChange={setClearBilling}
      />
      <Spacer size={32} />
      <Button
        className="mb-15 error"
        isBlock
        loading={mutation.isPending}
        buttonType="error"
        type="button"
        onClick={() => mutation.mutate()}
      >
        {t('device.deleteData.confirm')}
      </Button>
      <Button onClick={() => onClose()} disabled={mutation.isPending} isBlock buttonType="link">
        {t('global.cancel')}
      </Button>
    </Modal>
  );
}
